import React, { useContext, useEffect, useState } from "react";
import {
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import classNames from "classnames";
import Complete from "./checkout/Complete";
import Vendors from "./vendors";
import Order from "./checkout/Order";
import Sales from "./sales";
import Consignors from "./consignors";
import Cashiers from "./cashiers";
import ValidateAuth from "./ValidateAuth";
import { AuthContext, EntitiesContext, UserContext } from "../../App";
import { useGetLists } from "../../hooks/useList";
import { useGetSale } from "../../hooks/useSale";
import { useGetAccounts } from "../../hooks/useAccount";
import Loader from "../Loader";
import UserRecord from "../../records/user";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";

function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const entities = useContext(EntitiesContext);
  const auth = useContext(AuthContext);
  const user = useContext(UserContext);
  const sale = entities.state.get("sale").find((s) => s.open);
  const [toggleNav, setToggleNav] = useState(false);
  const icon = (
    <FontAwesomeIcon icon={faShoppingCart} className="icon is-large" />
  );
  const navIcon = <FontAwesomeIcon icon={faShoppingCart} />;

  useGetLists();
  const sales = useGetSale();
  const { isLoading } = useGetAccounts();

  useEffect(() => {
    if (user.user.role === "consigner") navigate("/dashboard");
  }, [user.user.role]);

  const logout = () => {
    window.localStorage.removeItem("auth");
    window.localStorage.removeItem("loginAsSession");
    auth.setAuth(null);
    navigate("/");
  };

  const returnToAdminLogin = () => {
    // Restore the original admin's auth
    const originalAuth = window.localStorage.getItem("originalAdminAuth");
    if (originalAuth) {
      const authData = JSON.parse(originalAuth);
      window.localStorage.setItem("auth", originalAuth);
      window.localStorage.removeItem("originalAdminAuth");
      window.localStorage.removeItem("loginAsSession");

      // Restore auth state
      auth.setAuth(authData.data.data);
      user.setUser(new UserRecord({ ...authData.data.data.attributes, is_logged_in: true }));

      navigate("/admin/cashiers");
    } else {
      // Fallback if original auth wasn't stored
      window.localStorage.removeItem("auth");
      window.localStorage.removeItem("loginAsSession");
      auth.setAuth(null);
      navigate("/");
    }
  };

  const isLoginAsSession = window.localStorage.getItem("loginAsSession") === "true";

  const showStartOrder = () => {
    return location.pathname === "/admin";
  };

  if (isLoading || !sales.isSuccess) return <Loader />;

  return (
    <div className="p-2" style={{ height: "100vh" }}>
      {isLoginAsSession && (
        <div className="notification is-warning" style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <strong>Logged in as:</strong> {user.user.first_name} {user.user.last_name}
            </span>
            <button className="button is-small is-link" onClick={returnToAdminLogin}>
              Return to Admin Login
            </button>
          </div>
        </div>
      )}
      {user.user.role === "admin" && (
        <nav
          className="navbar is-light"
          role="navigation"
          aria-label="main navigation"
        >
          <div className="navbar-brand">
            <span className="navbar-item">
              <span className="has-text-weight-semibold is-size-4">
                WES Ski & Skate Sale
              </span>
            </span>

            <a
              role="button"
              onClick={() => setToggleNav(!toggleNav)}
              className="navbar-burger"
              aria-label="menu"
              aria-expanded="false"
              data-target="navbarBasicExample"
            >
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
            </a>
          </div>

          <div
            id="navbarBasicExample"
            className={classNames("navbar-menu", {
              "is-active": toggleNav,
            })}
          >
            <div className="navbar-start">
              <div className="navbar-item has-dropdown is-hoverable">
                <Link
                  to="/admin/sales"
                  onClick={() => setToggleNav(false)}
                  className={classNames("navbar-item", {
                    "is-active": location.pathname.includes("sale"),
                  })}
                >
                  Sales
                </Link>

                <div className="navbar-dropdown">
                  <Link to="/admin/sales/new" className="navbar-item">
                    Add
                  </Link>

                  <Link to="/admin/sales/stats" className="navbar-item">
                    Stats
                  </Link>

                  <Link to="/admin/sales/orders" className="navbar-item">
                    Orders
                  </Link>

                  <Link to="/admin/sales/comms" className="navbar-item">
                    Communications
                  </Link>

                  <Link to="/admin/sales/debug" className="navbar-item">
                    Debug
                  </Link>
                </div>
              </div>

              <div className="navbar-item has-dropdown is-hoverable">
                <Link
                  to="/admin/cashiers"
                  onClick={() => setToggleNav(false)}
                  className="navbar-item"
                >
                  Cashiers
                </Link>

                <div className="navbar-dropdown">
                  <Link to="/admin/cashiers/add" className="navbar-item">
                    Add
                  </Link>
                  <Link to="/admin/cashiers" className="navbar-item">
                    Login
                  </Link>
                </div>
              </div>

              <div className="navbar-item has-dropdown is-hoverable">
                <Link
                  to="/admin/vendors"
                  onClick={() => setToggleNav(false)}
                  className={classNames("navbar-item", {
                    "is-active": location.pathname.includes("vendor"),
                  })}
                >
                  Vendors
                </Link>

                <div className="navbar-dropdown">
                  <Link to="/admin/vendors/new" className="navbar-item">
                    Add
                  </Link>
                  <Link to="/admin/vendors/print" className="navbar-item">
                    Print
                  </Link>
                  <Link to="/admin/vendors/reports" className="navbar-item">
                    Reports
                  </Link>
                </div>
              </div>

              <Link
                to="/admin/consignors"
                onClick={() => setToggleNav(false)}
                className={classNames("navbar-item", {
                  "is-active": location.pathname.includes("consignors"),
                })}
              >
                Consignors
              </Link>
            </div>

            <div className="navbar-end">
              <div className="navbar-item">
                <div className="buttons">
                  <Link
                    to="/admin"
                    onClick={() => setToggleNav(false)}
                    className={classNames("button is-link", {
                      "is-active": location.pathname === "/admin",
                    })}
                  >
                    <span className="icon is-small">{navIcon}</span>
                    <span>Checkout</span>
                  </Link>
                  <a className="button is-danger" onClick={logout}>
                    <strong>Logout</strong>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      <div className="mt-2">
        <Routes>
          <Route path="/cashiers/*" element={<Cashiers />} />
          <Route path="/vendors/*" element={<Vendors />} />
          <Route path="/sales/*" element={<Sales />} />
          <Route path="/order" element={<Order />} />
          <Route path="/complete" element={<Complete />} />
          <Route path="/consignors" element={<Consignors sale={sale} />} />
          <Route path="/auth/:token" element={<ValidateAuth />} />
        </Routes>
      </div>

      {showStartOrder() && (
        <div className="center has-text-centered">
          <Link to="/admin/order">
            {icon}
            <div className="is-size-3">New Order</div>
          </Link>
        </div>
      )}
    </div>
  );
}

export default Admin;
